import React from "react";
import {
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Typography,
    CircularProgress, Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslation } from "react-i18next";

import {
    FormSessionPrefillRequestDto,
    FormSessionPrefillRequestStateType,
} from "../../../client/formsession/FormSessionApiClient";
import {PrefillRequestStateFormatter} from "./PrefillRequestStateFormatter";
import {PrefillRequestInformationSystemFormatter} from "./PrefillRequestInformationSystemFormatter";

export interface PrefillRequestsStepperProps {
    prefillRequests: FormSessionPrefillRequestDto[];
    prefillRequestsTypeTitle?: string;
    prefillRequestsTypeDetail?: string;
}

export default function PrefillRequestsStepper(props: Readonly<PrefillRequestsStepperProps>) {
    const {prefillRequests, prefillRequestsTypeTitle, prefillRequestsTypeDetail} = props;
    const { t } = useTranslation();

    const getIcon = (state?: FormSessionPrefillRequestStateType): React.ReactNode => {
        switch (state) {
            case FormSessionPrefillRequestStateType.OBTAINED:
                return <CheckCircleIcon fontSize="small" color="success" />;
            case FormSessionPrefillRequestStateType.IN_PROGRESS:
                return <CircularProgress size={24} />;
            case FormSessionPrefillRequestStateType.FAILED:
                return <ErrorIcon fontSize="small" color="error" />;
            default:
                return <ErrorIcon fontSize="small" color="error" />;
        }
    };

    const getLabelColor = (
        state?: FormSessionPrefillRequestStateType
    ): "text.primary" | "success.main" | "error.main" => {
        switch (state) {
            case FormSessionPrefillRequestStateType.OBTAINED:
                return "success.main";
            case FormSessionPrefillRequestStateType.FAILED:
                return "error.main";
            default:
                return "text.primary";
        }
    };

    if (prefillRequests.length === 0) {
        return null;
    }

    return (
        <Stack spacing={2} direction={"column"}>
            <Typography variant="h5">
                {prefillRequestsTypeTitle}
            </Typography>
            <Typography variant="body2" sx={{textAlign: "justify", fontWeight: "bold"}}>
                {prefillRequestsTypeDetail}
            </Typography>
            <Stepper activeStep={-1} orientation="vertical" sx={{mb: 2}}>
                {prefillRequests.map((prefillRequest) => {
                    const icon = getIcon(prefillRequest.state);
                    const labelColor = getLabelColor(prefillRequest.state);
                    const content = PrefillRequestStateFormatter.formatPrefillRequestState(prefillRequest.state, t);
                    const informationSystem = PrefillRequestInformationSystemFormatter.formatInformationSystem(prefillRequest.informationSystem, t);
                    const informationDetail = prefillRequest.informationDetail?.trim();
                    const informationSystemWithDetail = informationDetail ? `${informationSystem} - ${informationDetail}` : informationSystem;

                    return (
                        <Step key={prefillRequest.id} expanded>
                            <StepLabel StepIconComponent={() => <>{icon}</>}
                                       sx={{color: labelColor}}>
                                {informationSystemWithDetail}
                            </StepLabel>
                            <StepContent>
                                <Typography variant="body2" fontStyle={"italic"}>
                                    {content}
                                </Typography>
                            </StepContent>
                        </Step>
                    );
                })}
            </Stepper>
        </Stack>
    );
};
